import React, { useCallback } from 'react';
import styles from './AdminUpsellBoardClosed.less';
import { Analytics } from '@trello/atlassian-analytics';
import { forNamespace } from '@trello/i18n';
import { DeleteBoardButton } from 'app/src/components/DeleteBoardButton';
import { useFreeTrialEligibilityRules } from 'app/src/components/FreeTrial/useFreeTrialEligibilityRules';
import { useUpgradePromptRules } from 'app/src/components/UpgradePrompts/useUpgradePromptRules';
import { Button } from '@trello/nachos/button';
import { isDesktop } from '@trello/browser';
import classNames from 'classnames';

interface AdminUpsellProps {
  boardId: string;
  boardName: string;
  orgId: string;
}

const f = forNamespace('closed board view', {
  shouldEscapeStrings: false,
});
const formatUpgrade = forNamespace([
  'upgrade-prompt-plan-selection-consolidated',
  'expired-free-trial-upgrade',
]);

export const AdminUpsellBoardClosed: React.FC<AdminUpsellProps> = ({
  boardId,
  orgId,
  boardName,
}) => {
  const { openPlanSelection } = useUpgradePromptRules(
    orgId,
    `closed-board-upsell-${orgId}`,
    { redirect: false },
  );
  const { isEligible, isExpired } = useFreeTrialEligibilityRules(orgId, {
    skip: !orgId,
  });

  const onClick = useCallback(() => {
    openPlanSelection();

    Analytics.sendClickedButtonEvent({
      buttonName: 'bcUpgradePrompt',
      source: 'closeBoardScreen',
      containers: {
        organization: {
          id: orgId,
        },
      },
      attributes: {
        isFreeTrial: !!isEligible,
        promptId: 'closeBoardUpgradeLink',
      },
    });
  }, [isEligible, openPlanSelection, orgId]);

  return (
    <div>
      <div
        className={classNames(styles.boardClosedSection, {
          [styles.notDesktop]: !isDesktop(),
          [styles.desktopVersion]: isDesktop(),
        })}
      >
        <h1 className={styles.headerText}>
          {f('board closed title', { boardName })}
        </h1>
        <p>
          {f('upsell reopen board info')}{' '}
          <a href="https://help.trello.com/article/781-reopening-a-closed-board">
            {f('upsell learn more link')}
          </a>
        </p>
        <>
          <div className={styles.deleteLink}>
            <DeleteBoardButton source="boardScreen" boardId={boardId} />
          </div>
        </>
      </div>
      {!isDesktop() ? (
        <div className={styles.upsellSection}>
          <div className={styles.upsellText}>
            <h2>{f('upsell max boards')}</h2>
            <p>{f('upsell instructions')}</p>
            <Button onClick={onClick} appearance="primary">
              {isExpired ? formatUpgrade('cta') : f('free trial cta')}
            </Button>
          </div>
          <img
            src={require('resources/images/closed-board-screen/admin-upsell.png')}
            alt=""
            role="presentation"
            className={styles.upsellImage}
          />
        </div>
      ) : null}
    </div>
  );
};
